(() => {
  const TIME_ZONE = "Europe/Zurich";
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

  const getParts = (timestamp) => {
    const parts = {};
    formatter.formatToParts(new Date(timestamp)).forEach(({ type, value }) => {
      if (type !== "literal") parts[type] = Number(value);
    });
    return parts;
  };

  const getOffset = (timestamp) => {
    const normalized = Math.floor(timestamp / 1000) * 1000;
    const parts = getParts(normalized);
    return Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    ) - normalized;
  };

  const normalizeMerchantDate = (dateValue) => {
    const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(dateValue || "");
    if (!match) return null;

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const calendarCheck = new Date(Date.UTC(year, month - 1, day));
    if (
      calendarCheck.getUTCFullYear() !== year ||
      calendarCheck.getUTCMonth() + 1 !== month ||
      calendarCheck.getUTCDate() !== day
    ) {
      return null;
    }

    return `${match[3]}-${match[2]}-${match[1]}`;
  };

  const toZurichTimestamp = (dateValue, timeValue) => {
    const normalizedDate = normalizeMerchantDate(dateValue);
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizedDate || "");
    const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeValue || "");
    if (!dateMatch || !timeMatch) return null;

    const wanted = {
      year: Number(dateMatch[1]),
      month: Number(dateMatch[2]),
      day: Number(dateMatch[3]),
      hour: Number(timeMatch[1]),
      minute: Number(timeMatch[2]),
      second: 0,
    };
    const calendarCheck = new Date(
      Date.UTC(wanted.year, wanted.month - 1, wanted.day)
    );
    if (
      calendarCheck.getUTCFullYear() !== wanted.year ||
      calendarCheck.getUTCMonth() + 1 !== wanted.month ||
      calendarCheck.getUTCDate() !== wanted.day ||
      wanted.hour > 23 ||
      wanted.minute > 59 ||
      wanted.second > 59
    ) {
      return null;
    }

    const wallClock = Date.UTC(
      wanted.year,
      wanted.month - 1,
      wanted.day,
      wanted.hour,
      wanted.minute,
      wanted.second
    );
    const offsets = new Set([
      getOffset(wallClock - 36 * 60 * 60 * 1000),
      getOffset(wallClock),
      getOffset(wallClock + 36 * 60 * 60 * 1000),
    ]);
    const matches = Array.from(offsets)
      .map((offset) => wallClock - offset)
      .filter((timestamp) => {
        const actual = getParts(timestamp);
        return Object.keys(wanted).every((key) => actual[key] === wanted[key]);
      })
      .sort((a, b) => a - b);

    return matches[0] ?? null;
  };

  window.ForceCountdown = Object.freeze({
    timeZone: TIME_ZONE,
    normalizeDate: normalizeMerchantDate,
    toTimestamp: toZurichTimestamp,
  });

  if (customElements.get("countdown-timer")) return;

  class Countdown extends HTMLElement {
    constructor() {
      super();
      this.enableMockTimer =
        this.getAttribute("data-enable-mock-timer") === "true";
      this.completedAction = this.getAttribute("data-completed");
      this.completedTarget =
        this.getAttribute("data-completed-target") || "countdown";
      this.completedText = this.getAttribute("data-completed-text");
      this.interval = null;
      this.completed = false;
      this.initCountdown();
    }

    initCountdown() {
      this.countdownBlocks = this.querySelector(".countdown__main");
      this.daysEl = this.querySelector(".countdown_block_days");
      this.hoursEl = this.querySelector(".countdown_block_hours");
      this.minutesEl = this.querySelector(".countdown_block_minutes");
      this.secondsEl = this.querySelector(".countdown_block_seconds");

      this.completedTextEl = document.createElement("div");
      this.completedTextEl.className = "countdown-completed-text";
      this.completedTextEl.hidden = true;
      this.countdownBlocks?.after(this.completedTextEl);

      // Optional absolute Shopify date_time; legacy Zurich/editor timers stay unchanged.
      if (this.hasAttribute("data-target-epoch")) {
        const epoch = this.getAttribute("data-target-epoch");
        this.targetTimestamp = /^\d+$/.test(epoch || "") ? Number(epoch) * 1000 : NaN;
      } else if (this.enableMockTimer) {
        const days = Number(
          this.getAttribute("data-enable-mock-picker-day") || 2
        );
        const hours = Number(
          this.getAttribute("data-enable-mock-picker-hours") || 3
        );
        if (!Number.isFinite(days) || !Number.isFinite(hours)) {
          this.invalidate();
          return;
        }
        this.targetTimestamp =
          Date.now() + (days * 24 + hours) * 60 * 60 * 1000;
      } else {
        this.targetTimestamp = toZurichTimestamp(
          this.getAttribute("data-date"),
          this.getAttribute("data-time")
        );
      }

      this.start();
    }

    start() {
      clearInterval(this.interval);
      this.interval = null;
      if (!Number.isFinite(this.targetTimestamp)) {
        this.invalidate();
        return;
      }
      this.style.removeProperty("display");
      this.style.removeProperty("visibility");
      this.countdownBlocks?.style.removeProperty("display");
      this.completedTextEl.hidden = true;
      this.completed = false;
      this.updateCountdown();
      if (!this.completed && this.hasAttribute("data-target-epoch")) {
        this.closest("[data-promotion-countdown]")?.style.removeProperty("display");
      }
      if (!this.completed && this.interval === null) {
        this.interval = setInterval(() => this.updateCountdown(), 1000);
      }
    }

    setTarget(dateValue, timeValue) {
      const nextTarget = toZurichTimestamp(dateValue, timeValue);
      this.setAttribute("data-date", dateValue ?? "");
      this.setAttribute("data-time", timeValue ?? "");
      if (nextTarget === this.targetTimestamp && this.interval) return true;
      this.targetTimestamp = nextTarget;
      this.start();
      return Number.isFinite(nextTarget);
    }

    invalidate() {
      this.stop();
      this.dispatchEvent(
        new CustomEvent("force:countdown-invalid", { bubbles: true })
      );
    }

    stop() {
      clearInterval(this.interval);
      this.interval = null;
      this.style.setProperty("display", "none", "important");
    }

    hideTarget() {
      let target = this;
      if (this.completedTarget === "section") {
        target = this.closest(".countdown-section") || this;
      } else if (this.completedTarget === "product-block") {
        target = this.closest(".main-product__countdown") || this;
      } else if (this.completedTarget === "promo-banner") {
        target = this.closest(".promo-banner-section") || this;
      } else if (this.completedTarget === "notification") {
        const banner = this.closest(".notification-banner__countdown");
        target = banner?.dataset.heading?.trim() ? this : banner || this;
      }
      target.style.setProperty("display", "none", "important");
    }

    complete() {
      if (this.completed) return;
      this.completed = true;
      clearInterval(this.interval);
      this.interval = null;

      if (this.completedAction === "hide_slide") {
        const slide = this.closest(".slideshow-swiper__slide");
        if (slide) slide.dataset.countdownExpired = "true";
        this.dispatchEvent(
          new CustomEvent("force:countdown-hide-slide", { bubbles: true })
        );
      } else if (this.completedAction === "release_phase") {
        const previousTarget = this.targetTimestamp;
        this.dispatchEvent(
          new CustomEvent("force:countdown-release-phase", { bubbles: true })
        );
        if (this.targetTimestamp === previousTarget) this.hideTarget();
      } else if (this.completedAction === "show_text") {
        this.countdownBlocks?.style.setProperty("display", "none");
        this.completedTextEl.hidden = false;
        this.completedTextEl.textContent = this.completedText || "";
      } else if (
        this.completedAction === "hide_section" ||
        this.completedAction === "hide_countdown"
      ) {
        this.hideTarget();
      } else {
        this.renderTime(0);
      }
    }

    renderTime(distance) {
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor(
        (distance % (1000 * 60 * 60)) / (1000 * 60)
      );
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      this.daysEl.textContent = String(days).padStart(2, "0");
      this.hoursEl.textContent = String(hours).padStart(2, "0");
      this.minutesEl.textContent = String(minutes).padStart(2, "0");
      this.secondsEl.textContent = String(seconds).padStart(2, "0");
    }

    updateCountdown() {
      const distance = this.targetTimestamp - Date.now();
      if (!Number.isFinite(distance)) {
        this.invalidate();
        return;
      }
      if (distance <= 0) {
        this.complete();
        return;
      }
      this.renderTime(distance);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  customElements.define("countdown-timer", Countdown);
  document.dispatchEvent(new CustomEvent("force:countdown-ready"));
})();
